import {
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ICaseStatisticsData } from '@bvi/api-interfaces/response/case';
import { DataPopover, Div } from '@bvi/common-components';

import { formatNumber } from '../../../lib/formatter';
import { i18nInstance } from '../../../locales';
import { styles } from '../styles';

const formatCaseCount = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }

  return formatNumber(value);
};

type ResultsCaseCountsProperties = {
  data: ICaseStatisticsData;
  isLoading?: boolean;
  shouldSticky?: boolean;
};

export const ResultsCaseCounts: React.FC<ResultsCaseCountsProperties> = (
  properties,
) => {
  const { data, isLoading, shouldSticky = false } = properties;
  const { t } = useTranslation('', {
    i18n: i18nInstance,
    keyPrefix: 'results.table',
  });

  const caseCountHeaders = [
    ['Total', 'Claims'],
    ['Claims Below Low', 'Quartile Mean (LQM)'],
    ['Claims Between', 'LQM & MHM'],
    ['Claims Between', 'MHM & HQM'],
    ['Claims Above High', 'Quartile Mean (HQM)'],
    ['Claims Between', 'LQM & HQM'],
  ];

  /** Bucket counts include $0 claimant payouts; thresholds match Key Metrics "Results Including $0s" (LQM / MHM / HQM). */
  const caseCountValues: Array<number | null | undefined> = [
    data.total,
    data.caseCountsBelowLowestQuartileAverage,
    data.caseCountsBetweenLowestQuartileAverageAndOverallAverage,
    data.caseCountsBetweenOverallAverageAndHighestQuartileAverage,
    data.caseCountsAboveHighestQuartileAverage,
    data.caseCountsBetweenLowestQuartileAverageAndHighestQuartileAverage,
  ];

  const wrapperStyles = shouldSticky ? styles.stickyWrapper : {};
  return (
    <Div sx={wrapperStyles}>
      <DataPopover title={t('caseCountsTitle')}>
        <Grid p={1}>
          <Table>
            <TableHead>
              <TableRow>
                {caseCountHeaders.map(([lineOne, lineTwo]) => (
                  <TableCell
                    key={`${lineOne}-${lineTwo}`}
                    sx={styles.headerCell}
                  >
                    <Div sx={styles.headerCellContent}>
                      <Div sx={styles.headerCellLabel}>{lineOne}</Div>
                      <Div sx={styles.headerCellLabel}>{lineTwo}</Div>
                    </Div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {caseCountValues.map((value, index) => (
                  <TableCell
                    key={`case-count-value-${index}`}
                    sx={styles.valueCell}
                  >
                    {isLoading ? <Skeleton /> : formatCaseCount(value)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
          <Div sx={styles.legend}>
            <Div sx={styles.legendItemCell}>LQM = Lowest Quartile Mean</Div>
            <Div sx={styles.legendItemCell}>MHM = Middle Half Mean</Div>
            <Div sx={styles.legendItemCell}>HQM = Highest Quartile Mean</Div>
          </Div>
        </Grid>
      </DataPopover>
    </Div>
  );
};
